import { LoadingIcon } from '@stormwater-software/lib-ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import reportDownloadApi from '../../../utils/api/reportDownload';

/**
 * SW2-2076 — download options for a generated report. Large reports
 * (> 150 MB) auto-download as the pre-built ZIP package; smaller ones
 * auto-download as the CDN PDF. Both buttons stay available so the user can
 * always pick the other format manually.
 *
 * Not every report has a ZIP (the findings report and document prints never
 * build one), so Download ZIP only shows once `zip-url` confirms it exists.
 */

/** Reports larger than this default to the ZIP package instead of the PDF. */
const ZIP_THRESHOLD_BYTES = 150 * 1024 * 1024;

/**
 * How long to wait for a fileSize after the download URL arrives before
 * defaulting the auto-download to PDF. Size-aware reports (e.g. findings)
 * send the size a beat after the URL — this window lets us pick ZIP vs PDF
 * correctly. Other jobs (e.g. inspections) never send a size, so after this
 * grace period we auto-download the PDF instead of stalling forever.
 */
const SIZE_WAIT_MS = 1500;

/** Native browser download — no fetch, so no CORS requirement on CloudFront. */
const downloadViaAnchor = (url) => {
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', '');
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener noreferrer');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const isNotFound = (err) => err?.response?.status === 404;

const OPTIONS = {
  pdf: {
    key: 'pdf',
    label: 'Download PDF',
    resolveUrl: (reportId) =>
      reportDownloadApi.getCdnUrl(reportId).then((r) => r.url),
  },
  zip: {
    key: 'zip',
    label: 'Download ZIP',
    resolveUrl: (reportId) =>
      reportDownloadApi.getZipUrl(reportId).then((r) => r.url),
  },
};

const DownloadOptions = ({ reportId, fileSize }) => {
  // keyed by option.key: { status: 'idle'|'running'|'done'|'error', error }
  const [results, setResults] = useState({});
  // null until the probe answers; false only on a 404, so a transient error
  // still leaves the button to retry.
  const [zipAvailable, setZipAvailable] = useState(null);
  const autoStarted = useRef(false);

  useEffect(() => {
    if (!reportId) return undefined;

    let cancelled = false;

    OPTIONS.zip.resolveUrl(reportId).then(
      () => !cancelled && setZipAvailable(true),
      (err) => !cancelled && setZipAvailable(!isNotFound(err)),
    );

    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const run = async (option) => {
    // Any download (auto or manual click) claims the auto-start slot, so a
    // pending grace-period timer won't fire a second, unwanted download.
    autoStarted.current = true;

    setResults((prev) => ({ ...prev, [option.key]: { status: 'running' } }));

    try {
      const url = await option.resolveUrl(reportId);

      downloadViaAnchor(url);

      setResults((prev) => ({ ...prev, [option.key]: { status: 'done' } }));
    } catch (err) {
      // No ZIP for this report (e.g. the > 150 MB auto-default): hand over
      // the PDF instead of an error.
      if (option.key === OPTIONS.zip.key && isNotFound(err)) {
        setZipAvailable(false);
        setResults(({ [option.key]: _dropped, ...rest }) => rest);
        run(OPTIONS.pdf);

        return;
      }

      setResults((prev) => ({
        ...prev,
        [option.key]: {
          status: 'error',
          error: (err && err.message) || 'Download failed',
        },
      }));
    }
  };

  // Auto-download the size-based default once the report is ready: reports
  // over the threshold use the ZIP package, everything else (including reports
  // that never report a size) uses the CDN PDF. The user can still override
  // via the buttons.
  useEffect(() => {
    if (autoStarted.current || !reportId) return;

    // The download URL (→ reportId) reaches us before fileSize does. If the
    // size is already known, decide immediately.
    if (typeof fileSize === 'number') {
      autoStarted.current = true;
      run(fileSize > ZIP_THRESHOLD_BYTES ? OPTIONS.zip : OPTIONS.pdf);

      return;
    }

    // Size not known yet: wait briefly for it. If it arrives this effect
    // re-runs (fileSize dep) and takes the branch above, cancelling this
    // timer. If it never arrives (jobs that don't report a size, e.g.
    // inspections), fall back to PDF so the download still auto-starts.
    const timer = setTimeout(() => {
      if (autoStarted.current) return;

      autoStarted.current = true;
      run(OPTIONS.pdf);
    }, SIZE_WAIT_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, fileSize]);

  return (
    <div className='download-options'>
      <ul className='download-options__list'>
        {Object.values(OPTIONS).map((option) => {
          const result = results[option.key] || { status: 'idle' };

          if (
            option.key === OPTIONS.zip.key &&
            !zipAvailable &&
            !results[option.key]
          ) {
            return null;
          }

          return (
            <li key={option.key} className='download-options__row'>
              <button
                type='button'
                className='reversed form-action-secondary'
                disabled={result.status === 'running' || !reportId}
                onClick={() => run(option)}
              >
                {option.label}
              </button>

              <span className='download-options__metrics'>
                {result.status === 'running' && <LoadingIcon />}

                {result.status === 'error' && (
                  <span className='download-options__error'>
                    {result.error}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

DownloadOptions.propTypes = {
  reportId: PropTypes.string,
  /** Report size in bytes; when > 150 MB the ZIP package is the default. */
  fileSize: PropTypes.number,
};

export default DownloadOptions;
